import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, FloppyDisk, Plus, Trash, Buildings, UserCircle, Warning, CheckCircle, CaretUp, CaretDown } from "@phosphor-icons/react";
import api from "@/lib/api";

const today = new Date().toISOString().split("T")[0];
const defaultExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
const PROPOSAL_NOTES_DEFAULT = "";
const SECTIONS = [
  { key: "user_licenses", label: "User Licenses", pricingLabel: "(Monthly)", color: "bg-blue-700", textCls: "text-white" },
  { key: "optional_modules", label: "Optional Modules", pricingLabel: "(Monthly)", color: "bg-purple-700", textCls: "text-white" },
  { key: "implementation_fees", label: "Implementation Fees", pricingLabel: "(One-Off)", color: "bg-green-700", textCls: "text-white" },
  { key: "services", label: "Services", pricingLabel: "(One-Off)", color: "bg-amber-700", textCls: "text-white" },
];

const emptyLine = (currency, cat) => ({ product_id: "", product_code_snapshot: "", description_snapshot: "", quantity: 1, unit_price: 0, line_currency_code: currency, line_discount_amount: 0, category_snapshot: cat, pricing_type_snapshot: (cat === "implementation_fees" || cat === "services") ? "one_off" : "annual", monthly_addon_unit_price: 0 });
const inputCls = "w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]";
const fmt = (n, c) => { try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: c || "USD" }).format(n ?? 0); } catch { return `${c} ${(n ?? 0).toFixed(2)}`; } };
// Display gates use round2(x) !== 0 rather than x > 0 so a credit line
// (negative amount) still renders its subtotal row instead of silently
// vanishing while the grand total quietly shrinks.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const lineSub = (l) => ((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)) - (Number(l.line_discount_amount) || 0);
const lineMo = (l) => { const pt = l.pricing_type_snapshot || "annual"; const s = lineSub(l); return pt === "monthly" ? s : pt === "annual" ? s / 12 : 0; };
const lineYr = (l) => { const pt = l.pricing_type_snapshot || "annual"; const s = lineSub(l); return pt === "annual" ? s : pt === "monthly" ? s * 12 : 0; };
const lineOO = (l) => (l.pricing_type_snapshot || "annual") === "one_off" ? lineSub(l) : 0;

export default function QuoteBuilder() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = !!quoteId;
  // `?type=adhoc&clientId=<id>` on a fresh /quotes/new URL pre-fills the
  // builder for an existing-client ad-hoc proposal. Lets the new
  // "+ New Ad-hoc Quote" button on the client detail page deeplink
  // straight into the right mode without the marketer having to flip
  // a radio by hand afterwards.
  const initialType = isEdit ? "new_customer" : (searchParams.get("type") === "adhoc" ? "adhoc" : "new_customer");
  const initialClientId = !isEdit ? (searchParams.get("clientId") || "") : "";
  const [recipientType, setRecipientType] = useState("client");
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ client_id: initialClientId, lead_id: "", currency_code: "USD", quote_date: today, expiry_date: defaultExpiry, reference_number: "", discount_amount: 0, tax_amount: 0, contract_months: 36, quote_type: initialType, include_recurring_clause: false, notes_internal: "", notes_external: "", standard_notes: PROPOSAL_NOTES_DEFAULT, recipient_first_name: "", recipient_last_name: "", billing_email: "", recipient_contact_role: null, show_year1_total: true, show_line_totals_horizontal: true, show_subsection_totals: true, show_document_total: true });
  const [lines, setLines] = useState([emptyLine("USD", "user_licenses")]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [validation, setValidation] = useState(null); // {valid, missing_fields, country, currency_code, currency_symbol}
  const [validating, setValidating] = useState(false);
  const [loadError, setLoadError] = useState(""); // shown if recipient lists fail
  // Suggested contact list for the linked client. Loads PM / billing /
  // primary names so the operator can one-click pick the right person
  // instead of typing it (Mike's report May 2026 — proposal #1096
  // silently defaulted to "Gaven" from billing_contact_name when the
  // PM was "Daniel").
  const [contactSuggestions, setContactSuggestions] = useState([]);

  useEffect(() => {
    api.get("/mgmt/clients?status=active")
      .then(r => setClients(Array.isArray(r.data) ? r.data : []))
      .catch(err => {
        const msg = err.response?.status === 403
          ? "You don't have permission to list clients. Ask an admin to grant you the Client Management module."
          : "Could not load the client list — please refresh the page.";
        setLoadError(msg);
      });
    api.get("/mgmt/products?is_active=true").then(r => setProducts(r.data)).catch(() => {});
    api.get("/crm/leads?is_active=true").then(r => setLeads(r.data)).catch(() => {});
    if (!isEdit) {
      api.get("/admin/standard-notes-rendered").then(r => {
        if (r.data.rendered) setForm(prev => ({ ...prev, standard_notes: r.data.rendered }));
      }).catch(() => {});
    }
    if (isEdit) {
      api.get(`/quotes/${quoteId}`).then(r => {
        const { quote, line_items } = r.data;
        setRecipientType(quote.lead_id && !quote.client_id ? "lead" : "client");
        setForm({ client_id: quote.client_id || "", lead_id: quote.lead_id || "", currency_code: quote.currency_code, quote_date: quote.quote_date, expiry_date: quote.expiry_date || "", reference_number: quote.reference_number || "", discount_amount: quote.discount_amount, tax_amount: quote.tax_amount, contract_months: quote.contract_months || 36, quote_type: quote.quote_type || "new_customer", include_recurring_clause: !!quote.include_recurring_clause, notes_internal: quote.notes_internal || "", notes_external: quote.notes_external || "", standard_notes: quote.standard_notes ?? PROPOSAL_NOTES_DEFAULT, recipient_first_name: quote.recipient_first_name || "", recipient_last_name: quote.recipient_last_name || "",
          // fix/proposal-recipient-contact-sync — carry the quote's OWN
          // stored email forward on load so a plain re-save can't wipe it;
          // recipient_contact_role is transient (never persisted) and only
          // gets set again if the operator clicks a Greet-As chip below.
          billing_email: quote.billing_email || "", recipient_contact_role: null,
          show_year1_total: quote.show_year1_total === undefined || quote.show_year1_total === null ? true : !!quote.show_year1_total, show_line_totals_horizontal: quote.show_line_totals_horizontal === undefined || quote.show_line_totals_horizontal === null ? true : !!quote.show_line_totals_horizontal, show_subsection_totals: quote.show_subsection_totals === undefined || quote.show_subsection_totals === null ? true : !!quote.show_subsection_totals, show_document_total: quote.show_document_total === undefined || quote.show_document_total === null ? true : !!quote.show_document_total });
        setLines(line_items.length > 0 ? line_items : [emptyLine(quote.currency_code, "user_licenses")]);
        // On edit, existing quotes already had complete data — just resolve currency for display.
        if (quote.client_id || quote.lead_id) {
          validateRecipient(quote.client_id || "", quote.lead_id || "");
        }
      }).finally(() => setLoading(false));
    }
  }, [quoteId, isEdit]);

  const validateRecipient = async (clientId, leadId) => {
    if (!clientId && !leadId) { setValidation(null); setContactSuggestions([]); return; }
    setValidating(true);
    try {
      const res = await api.get("/quotes/validate-recipient", { params: { client_id: clientId || undefined, lead_id: leadId || undefined } });
      setValidation(res.data);
      // Auto-apply the resolved currency to form & lines
      if (res.data.currency_code) {
        setForm(prev => ({ ...prev, currency_code: res.data.currency_code }));
        setLines(prev => prev.map(l => ({ ...l, line_currency_code: res.data.currency_code })));
      }
    } catch {
      /* swallow — validation surface handles its own errors */
    } finally {
      setValidating(false);
    }
    // Build contact suggestions. Order matters — the FIRST entry is the
    // recommended default that auto-prefills the recipient name fields
    // (Session 81 follow-up — Mike asked for the New Proposal screen to
    // never require typing the name from scratch when the underlying
    // lead/client record already has one).
    //
    // We dedupe by lowercased full name so the same person who is both
    // the PM and the Billing contact only shows up once.
    const joinName = (f, l) => [f, l].map(s => (s || "").trim()).filter(Boolean).join(" ");
    // Smart-split "FirstLast" pascal-cased strings that arrived in a
    // single field (e.g. a legacy import stored billing_first_name as
    // "DeniseCruz" with no space). Only splits when BOTH halves are
    // 3+ chars so "McDonald", "DeLuca", "DiAngelo", "O'Brien" are
    // never destroyed. Returns [first, last] or null.
    const splitIfConcat = (s) => {
      const m = (s || "").match(/^([A-Z][a-z]{2,})([A-Z][a-z]{2,}.*)$/);
      return m ? [m[1], m[2]] : null;
    };
    const buildContact = (firstRaw, lastRaw, fullRaw) => {
      let first = (firstRaw || "").trim();
      let last = (lastRaw || "").trim();
      if (!first && !last && fullRaw) {
        const parts = fullRaw.trim().split(/\s+/);
        first = parts[0] || "";
        last = parts.slice(1).join(" ");
      }
      // If we ended up with a single concatenated token (no spaces) in
      // `first` and nothing in `last`, try the camelCase split.
      if (first && !last && !/\s/.test(first)) {
        const split = splitIfConcat(first);
        if (split) { first = split[0]; last = split[1]; }
      }
      return { first, last, full: joinName(first, last) || (fullRaw || "").trim() };
    };
    let candidates = [];
    if (clientId) {
      try {
        const cr = await api.get(`/mgmt/clients/${clientId}`);
        // `/api/mgmt/clients/{id}` wraps its payload as `{ client: {...} }`.
        // The auto-prefill heuristic below reads pm_/billing_/contact_*
        // fields from the inner object — unwrap it (with a backward-compat
        // fallback to a flat shape) so the suggestions actually populate.
        const c = (cr.data && cr.data.client) ? cr.data.client : (cr.data || {});
        const pm = buildContact(c.pm_first_name, c.pm_last_name, c.pm_contact_name);
        const billing = buildContact(c.billing_first_name, c.billing_last_name, c.billing_contact_name);
        const primary = buildContact(c.contact_first_name, c.contact_last_name, c.contact_person);
        // `role` is the wire token sent to the backend alongside the
        // picked name/email so it can re-resolve the authoritative pair
        // from the client record itself (fix/proposal-recipient-contact-sync)
        // instead of trusting these values blindly.
        candidates = [
          { label: "Project Manager", short: "PM", role: "pm",
            first: pm.first, last: pm.last, full: pm.full,
            email: (c.pm_contact_email || "").trim(), recommended: true },
          { label: "Billing contact", short: "Billing", role: "billing",
            first: billing.first, last: billing.last, full: billing.full,
            email: (c.billing_email || "").trim() },
          { label: "Primary contact", short: "Primary", role: "primary",
            first: primary.first, last: primary.last, full: primary.full,
            email: (c.contact_email || "").trim() },
        ];
      } catch {
        candidates = [];
      }
    } else if (leadId) {
      // Leads expose a single contact triple (first/last/person). We
      // build a one-entry suggestion list so the UI is symmetric with
      // the client path. The `leads` list endpoint already returns
      // decrypted name fields.
      const lead = leads.find(x => x.lead_id === leadId);
      if (lead) {
        const c = buildContact(lead.contact_first_name, lead.contact_last_name, lead.contact_person);
        if (c.full) {
          candidates = [{
            label: "Lead contact", short: "Lead", role: null, // leads have one contact slot — no role ambiguity to resolve server-side
            first: c.first, last: c.last, full: c.full,
            email: (lead.contact_email || "").trim(),
            recommended: true,
          }];
        }
      }
    }
    candidates = candidates.filter(x => x.full); // hide empties
    const seen = new Set();
    const deduped = candidates.filter(x => {
      const key = x.full.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    setContactSuggestions(deduped);

    // Auto-prefill: if the recipient fields are still empty AND we
    // have a recommended (or first available) suggestion, drop it in.
    // We NEVER overwrite a user-typed value — typing is always wins
    // over the heuristic, consistent with the existing "never set
    // this to a guess" copy below the inputs.
    if (deduped.length > 0) {
      setForm(prev => {
        if ((prev.recipient_first_name || "").trim()
            || (prev.recipient_last_name || "").trim()) {
          return prev;  // user already typed something, don't clobber
        }
        const pick = deduped.find(c => c.recommended) || deduped[0];
        return {
          ...prev,
          recipient_first_name: pick.first || "",
          recipient_last_name: pick.last || "",
        };
      });
    }
  };

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const onClientChange = (cid) => {
    setF("client_id", cid);
    // A previously-picked Greet-As role belonged to the OLD client's
    // contact record — it must not silently resolve against a different
    // client on submit (fix/proposal-recipient-contact-sync).
    setF("recipient_contact_role", null);
    const c = clients.find(x => x.client_id === cid);
    if (c && c.currency_code) setF("currency_code", c.currency_code);
    validateRecipient(cid, null);
  };
  const onLeadChange = (lid) => {
    setF("lead_id", lid);
    setF("recipient_contact_role", null);
    validateRecipient(null, lid);
  };
  const onProductSelect = (idx, pid, sectionKey) => {
    const p = products.find(x => x.product_id === pid);
    if (p) setLines(prev => prev.map((l, i) => i !== idx ? l : { ...l, product_id: p.product_id, product_code_snapshot: p.product_code, description_snapshot: p.name, unit_price: p.current_selling_price, line_currency_code: p.price_currency_code || form.currency_code, category_snapshot: p.category || sectionKey, pricing_type_snapshot: p.pricing_type || (sectionKey === "implementation_fees" ? "one_off" : "annual") }));
    else setLines(prev => prev.map((l, i) => i !== idx ? l : { ...l, product_id: "" }));
  };
  const setLine = (idx, field, val) => setLines(prev => prev.map((l, i) => i !== idx ? l : { ...l, [field]: val }));
  const addLine = (sk) => setLines(prev => [...prev, emptyLine(form.currency_code, sk)]);
  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));
  // Move a line up or down WITHIN its own category. direction: -1 = up, +1 = down.
  const moveLine = (idx, direction) => setLines(prev => {
    const cat = prev[idx]?.category_snapshot;
    if (!cat) return prev;
    // Indices (in the flat `lines` array) of all items in this section, in order.
    const sectionIdxs = prev.map((l, i) => l.category_snapshot === cat ? i : -1).filter(i => i !== -1);
    const pos = sectionIdxs.indexOf(idx);
    const target = pos + direction;
    if (target < 0 || target >= sectionIdxs.length) return prev;
    const a = sectionIdxs[pos];
    const b = sectionIdxs[target];
    const next = prev.slice();
    [next[a], next[b]] = [next[b], next[a]];
    return next;
  });

  const totalMo = lines.reduce((s, l) => s + lineMo(l), 0);
  const totalYr = lines.reduce((s, l) => s + lineYr(l), 0);
  const totalOO = lines.reduce((s, l) => s + lineOO(l), 0);
  const annualTotal = totalYr + totalOO - Number(form.discount_amount || 0) + Number(form.tax_amount || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (validation && !validation.valid) { setError("Cannot create proposal — recipient record is missing required information (see banner above)."); return; }
    if (lines.some(l => !l.product_code_snapshot || !l.description_snapshot)) { setError("Each line needs a code and description"); return; }
    // Credits are entered as a signed line (negative quantity OR negative
    // unit price). Both negative multiplies back to a POSITIVE line total,
    // silently turning an intended credit into a charge — reject it.
    if (lines.some(l => Number(l.quantity) < 0 && Number(l.unit_price) < 0)) {
      setError("Use a negative quantity or a negative unit price for a credit, but not both.");
      return;
    }
    if (lines.some(l => Number(l.quantity) === 0)) { setError("Quantity cannot be zero."); return; }
    if (lines.some(l => !Number.isFinite(Number(l.quantity)) || !Number.isFinite(Number(l.unit_price)))) {
      setError("Quantity and unit price must be valid numbers.");
      return;
    }
    setSaving(true); setError("");
    try {
      const payload = { ...form, client_id: recipientType === "client" ? (form.client_id || null) : null, lead_id: recipientType === "lead" ? (form.lead_id || null) : null, discount_amount: Number(form.discount_amount), tax_amount: Number(form.tax_amount),
        // Ad-hoc proposals don't carry a contract term; null it out so the
        // PDF renderer knows to collapse the Year-1/ongoing-annual section
        // into a single Total. Otherwise fall back to the 36-month default.
        contract_months: form.quote_type === "adhoc" ? null : Number(form.contract_months || 36),
        quote_type: form.quote_type || "new_customer",
        include_recurring_clause: !!form.include_recurring_clause,
        show_year1_total: !!form.show_year1_total,
        show_line_totals_horizontal: !!form.show_line_totals_horizontal,
        show_subsection_totals: !!form.show_subsection_totals,
        show_document_total: !!form.show_document_total,
        line_items: lines.map(l => ({ product_id: l.product_id || null, product_code_snapshot: l.product_code_snapshot, description_snapshot: l.description_snapshot, quantity: Number(l.quantity), unit_price: Number(l.unit_price), line_currency_code: l.line_currency_code || form.currency_code, line_discount_amount: Number(l.line_discount_amount || 0), category_snapshot: l.category_snapshot || "user_licenses", pricing_type_snapshot: l.pricing_type_snapshot || "annual", monthly_addon_unit_price: l.category_snapshot === "services" ? Number(l.monthly_addon_unit_price || 0) : 0 })) };
      if (isEdit) { await api.put(`/quotes/${quoteId}`, payload); navigate(`/quotes/${quoteId}`); }
      else { const res = await api.post("/quotes", payload); navigate(`/quotes/${res.data.quote_id}`); }
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "RECIPIENT_INCOMPLETE") {
        setError(`${d.message} Missing: ${(d.missing_fields || []).join(", ")}`);
      } else if (d && typeof d === "object" && d.message) {
        // Covers RECIPIENT_CONTACT_NO_EMAIL and other structured backend
        // errors (fix/proposal-recipient-contact-sync) — show the safe
        // message instead of masking it behind a generic failure.
        setError(d.message);
      } else {
        setError(typeof d === "string" ? d : "Failed to save");
      }
    }
    setSaving(false);
  };

  if (loading) return <div className="p-6"><div className="h-4 bg-[#F5F5F5] animate-pulse w-48" /></div>;

  return (
    <div className="p-6" data-testid="quote-builder-page">
      <Link to="/quotes" className="inline-flex items-center gap-1.5 text-sm text-[#A3A3A3] hover:text-[#0A0A0A] mb-5"><ArrowLeft size={15} /> Back to Proposals</Link>
      <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight mb-6">{isEdit ? "Edit Proposal" : "New Proposal"}</h1>

      <form onSubmit={submit} className="space-y-5 max-w-6xl">
        {/* Header */}
        <div className="bg-white border border-[#E5E5E5] p-5">
          <h2 className="font-bold text-sm text-[#525252] uppercase tracking-wider mb-4">Proposal Details</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="col-span-2 lg:col-span-1">
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">For <span className="text-red-500">*</span></label>
              <div className="flex gap-1 mb-2 bg-[#F5F5F5] p-0.5 w-fit">
                <button type="button" onClick={() => { setRecipientType("client"); setF("lead_id", ""); }} data-testid="recipient-type-client"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${recipientType === "client" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#A3A3A3]"}`}>
                  <Buildings size={12} /> Client
                </button>
                <button type="button" onClick={() => { setRecipientType("lead"); setF("client_id", ""); }} data-testid="recipient-type-lead"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${recipientType === "lead" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#A3A3A3]"}`}>
                  <UserCircle size={12} /> Lead
                </button>
              </div>
              {recipientType === "client" ? (
                <select value={form.client_id} onChange={e => onClientChange(e.target.value)} required className={inputCls} data-testid="quote-client-select">
                  <option value="">{loadError ? "Could not load clients" : (clients.length === 0 ? "Loading clients…" : "Select client...")}</option>
                  {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.legal_name || c.name}</option>)}
                </select>
              ) : (
                <select value={form.lead_id} onChange={e => onLeadChange(e.target.value)} required className={inputCls} data-testid="quote-lead-select">
                  <option value="">Select lead...</option>
                  {leads.map(l => <option key={l.lead_id} value={l.lead_id}>{l.company_name}{l.contact_person ? ` - ${l.contact_person}` : ""}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Currency <span className="font-normal normal-case text-[10px] text-[#A3A3A3]">(auto)</span></label>
              <div className="h-[38px] border border-[#E5E5E5] bg-[#F8FAFC] px-3 flex items-center gap-2" data-testid="quote-currency-display">
                <span className="font-mono font-bold text-sm text-[#0055FF]">{form.currency_code}</span>
                {validation?.country && <span className="text-[10px] text-[#A3A3A3] truncate">· {validation.country}</span>}
              </div>
            </div>
            <div><label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Date <span className="text-red-500">*</span></label><input type="date" value={form.quote_date} onChange={e => setF("quote_date", e.target.value)} required className={inputCls} /></div>
            <div><label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Expiry</label><input type="date" value={form.expiry_date} onChange={e => setF("expiry_date", e.target.value)} className={inputCls} /></div>
            <div><label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Reference / PO</label><input value={form.reference_number} onChange={e => setF("reference_number", e.target.value)} placeholder="PO-1234" className={inputCls} /></div>
            {form.quote_type !== "adhoc" && (
              <div><label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Contract (months)</label><input type="number" min={1} max={120} value={form.contract_months} onChange={e => setF("contract_months", e.target.value)} className={inputCls} data-testid="quote-contract-months" /></div>
            )}
            <div><label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Discount</label><input type="number" min={0} step={0.01} value={form.discount_amount} onChange={e => setF("discount_amount", e.target.value)} className={inputCls} /></div>
            <div><label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Tax</label><input type="number" min={0} step={0.01} value={form.tax_amount} onChange={e => setF("tax_amount", e.target.value)} className={inputCls} /></div>
          </div>

          {/* Proposal Display Checkboxes sprint (2026-07-07) + Year-1
              checkbox relocation follow-up (2026-07-07) — all four
              presentation-only toggles for the customer-facing proposal
              document (preview/PDF/email/public link) now live in one
              row. All default ON so every proposal renders exactly as
              before unless explicitly turned off. Underlying pricing/
              quantities are never touched — see backend _proposal_html()
              for the render gating. `show_year1_total` is the pre-
              existing Session 91 iter-5 field (moved here from its own
              standalone block, not a new field) — uncheck to omit the
              rolled-up "Total (Year 1)" row (and its supporting Monthly/
              Annual/One-off breakdown) entirely. */}
          <div className="mt-3 pt-3 border-t border-[#F5F5F5]">
            <label className="block text-xs font-bold text-[#525252] mb-2 uppercase tracking-wider">Proposal Display</label>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#525252] hover:text-[#0A0A0A]" data-testid="show-line-totals-label">
                <input
                  type="checkbox"
                  checked={!!form.show_line_totals_horizontal}
                  onChange={e => setF("show_line_totals_horizontal", e.target.checked)}
                  className="accent-[#0055FF]"
                  data-testid="show-line-totals-checkbox"
                />
                <span>Show Line Totals horizontally</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#525252] hover:text-[#0A0A0A]" data-testid="show-subsection-totals-label">
                <input
                  type="checkbox"
                  checked={!!form.show_subsection_totals}
                  onChange={e => setF("show_subsection_totals", e.target.checked)}
                  className="accent-[#0055FF]"
                  data-testid="show-subsection-totals-checkbox"
                />
                <span>Show Subsection Totals</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#525252] hover:text-[#0A0A0A]" data-testid="show-document-total-label">
                <input
                  type="checkbox"
                  checked={!!form.show_document_total}
                  onChange={e => setF("show_document_total", e.target.checked)}
                  className="accent-[#0055FF]"
                  data-testid="show-document-total-checkbox"
                />
                <span>Document Total</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#525252] hover:text-[#0A0A0A]" data-testid="show-year1-total-label">
                <input
                  type="checkbox"
                  checked={!!form.show_year1_total}
                  onChange={e => setF("show_year1_total", e.target.checked)}
                  className="accent-[#0055FF]"
                  data-testid="show-year1-total-checkbox"
                />
                <span>Show Total (Year 1) row on the printed proposal</span>
              </label>
            </div>
          </div>

          {/* S64 — Recipient person name. Drives "Dear ___," in every
              email this proposal generates. Decoupled from the linked
              client/lead so a wrong link can never poison the salutation. */}
          <div className="mt-3 pt-3 border-t border-[#F5F5F5]">
            <label className="block text-xs font-bold text-[#525252] mb-1.5 uppercase tracking-wider">
              Recipient person name <span className="font-normal normal-case text-[10px] text-[#A3A3A3]">— shown as "Dear &lt;First Name&gt;," on every email</span>
            </label>
            {/* Suggested-contact pills. Click to one-shot populate the
                first + last name inputs. PM is marked recommended. */}
            {contactSuggestions.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="contact-suggestions">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Suggested:</span>
                {contactSuggestions.map((s) => {
                  const active = (form.recipient_first_name || "").trim().toLowerCase() === (s.first || "").toLowerCase()
                                 && (form.recipient_last_name || "").trim().toLowerCase() === (s.last || "").toLowerCase()
                                 && (form.billing_email || "").trim().toLowerCase() === (s.email || "").toLowerCase();
                  const hasEmail = !!s.email;
                  return (
                    <button
                      type="button"
                      key={s.label}
                      disabled={!hasEmail}
                      onClick={() => {
                        if (!hasEmail) {
                          // fix/proposal-recipient-contact-sync — never let a
                          // contact with no email on file silently inherit a
                          // different contact's address. Block the pick and
                          // say why, instead of leaving a stale/mismatched
                          // billing_email in place.
                          setError(`${s.label} contact has no email address on file. Add one to the client record, or choose a different contact.`);
                          return;
                        }
                        setError("");
                        setForm(prev => ({
                          ...prev,
                          recipient_first_name: s.first || "",
                          recipient_last_name: s.last || "",
                          billing_email: s.email || "",
                          recipient_contact_role: s.role || null,
                        }));
                      }}
                      title={hasEmail ? `${s.full} · ${s.label} · ${s.email}` : `${s.full} · ${s.label} · no email on file`}
                      className={`h-7 px-2 text-[11px] font-bold border transition-colors flex items-center gap-1 ${
                        !hasEmail
                          ? "bg-[#F5F5F5] text-[#A3A3A3] border-[#E5E5E5] cursor-not-allowed opacity-70"
                          : active
                          ? "bg-[#0055FF] text-white border-[#0055FF]"
                          : s.recommended
                            ? "bg-[#F0FDF4] text-[#16A34A] border-[#BBF7D0] hover:border-[#16A34A]"
                            : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0055FF]"
                      }`}
                      data-testid={`contact-suggest-${s.short.toLowerCase()}`}
                    >
                      {s.recommended && !active && <span className="text-[8px] uppercase tracking-wider">★</span>}
                      <span className="font-bold">{s.full}</span>
                      <span className={`text-[9px] font-normal uppercase tracking-wider ${active ? "opacity-70" : "opacity-60"}`}>· {s.short}</span>
                      {!hasEmail && <span className="text-[8px] uppercase tracking-wider">(no email)</span>}
                    </button>
                  );
                })}
                <span className="text-[10px] text-[#A3A3A3] ml-1">★ recommended</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                value={form.recipient_first_name}
                onChange={e => setForm(prev => ({ ...prev, recipient_first_name: e.target.value, recipient_contact_role: null }))}
                placeholder="First name (e.g. Thaddeus)"
                className={inputCls}
                data-testid="quote-recipient-first-name"
                maxLength={100}
              />
              <input
                value={form.recipient_last_name}
                onChange={e => setForm(prev => ({ ...prev, recipient_last_name: e.target.value, recipient_contact_role: null }))}
                placeholder="Last name (e.g. Kelly)"
                className={inputCls}
                data-testid="quote-recipient-last-name"
                maxLength={100}
              />
            </div>
            <p className="text-[10px] text-[#A3A3A3] mt-1.5">
              Auto-filled from the linked {recipientType === "lead" ? "lead" : "client"} record. Click a suggestion above to swap, or type your own. Leave blank to use "Hello," instead.
            </p>
          </div>

          {/* Proposal Type + Ad-hoc Recurring-Clause Checkbox
              Moved below the primary metadata grid so it sits close to the
              line-item builder — marketers see its effect immediately
              when they toggle it (contract term disappears, totals
              collapse to a single Total in the PDF preview). */}
          <div className="mt-4 pt-4 border-t border-[#E5E5E5] flex items-start gap-6 flex-wrap">
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1.5 uppercase tracking-wider">Proposal Type <span className="text-red-500">*</span></label>
              <div className="inline-flex items-center bg-[#F8FAFC] border border-[#E5E5E5] p-0.5" data-testid="quote-type-toggle">
                <button type="button" onClick={() => setForm(p => ({ ...p, quote_type: "new_customer" }))} data-testid="quote-type-new-customer"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${form.quote_type !== "adhoc" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#A3A3A3]"}`}>
                  <Buildings size={12} /> New Customer
                </button>
                <button type="button" onClick={() => setForm(p => ({ ...p, quote_type: "adhoc" }))} data-testid="quote-type-adhoc"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${form.quote_type === "adhoc" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#A3A3A3]"}`}>
                  <UserCircle size={12} /> Existing · Ad-hoc
                </button>
              </div>
              <p className="text-[10px] text-[#A3A3A3] mt-1.5 max-w-xs leading-relaxed">
                {form.quote_type === "adhoc"
                  ? "Existing client. PDF omits contract term, Year-1 total, and ongoing-annual breakdown."
                  : "Full new-customer proposal with contract term, Year-1 total, and ongoing-annual breakdown."}
              </p>
            </div>

            {form.quote_type === "adhoc" && (
              <div className="flex-1 min-w-[260px]" data-testid="adhoc-clause-block">
                <label className="block text-xs font-bold text-[#525252] mb-1.5 uppercase tracking-wider">Optional Clause</label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.include_recurring_clause}
                         onChange={e => setF("include_recurring_clause", e.target.checked)}
                         className="mt-0.5"
                         data-testid="include-recurring-clause" />
                  <span className="text-xs text-[#0A0A0A] leading-tight">
                    Include: <em>"An annually recurring support fee for this work may be applied."</em>
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Recipient list load error */}
        {loadError && (
          <div className="bg-amber-50 border border-amber-300 px-4 py-3 text-xs text-amber-800" data-testid="recipient-list-error">{loadError}</div>
        )}

        {/* Recipient Validation Banner */}
        {validating && (
          <div className="bg-[#F5F5F5] border border-[#E5E5E5] px-4 py-3 text-xs text-[#525252]" data-testid="validation-checking">Checking recipient information…</div>
        )}
        {!validating && validation && !validation.valid && (
          <div className="bg-red-50 border-2 border-red-400 px-4 py-3" data-testid="validation-incomplete">
            <div className="flex items-start gap-3">
              <Warning size={20} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-red-800 uppercase tracking-wider">Cannot create proposal</p>
                <p className="text-xs text-red-700 mt-1">
                  This {validation.recipient_type} record is missing required information. A proposal cannot be drawn up until all of the following are captured so the correct currency is applied:
                </p>
                <ul className="text-xs text-red-800 mt-2 list-disc pl-5 font-medium space-y-0.5">
                  {validation.missing_fields.map(f => <li key={f}>{f}</li>)}
                </ul>
                <Link
                  to={validation.recipient_type === "client" ? `/clients/${form.client_id}` : `/crm/leads/${form.lead_id}`}
                  className="inline-block mt-2 text-xs font-bold text-red-700 hover:text-red-900 underline uppercase tracking-wider"
                  data-testid="edit-recipient-link">
                  Open {validation.recipient_type} record to fill missing details →
                </Link>
              </div>
            </div>
          </div>
        )}
        {!validating && validation && validation.valid && (
          <div className="bg-green-50 border border-green-300 px-4 py-2.5 flex items-center gap-2" data-testid="validation-ok">
            <CheckCircle size={16} weight="fill" className="text-green-600 shrink-0" />
            <p className="text-xs text-green-800">
              <span className="font-bold">{validation.country}</span> · Proposal will be drawn up in <span className="font-mono font-bold">{validation.currency_code}</span> ({validation.currency_symbol.trim()})
            </p>
          </div>
        )}

        {/* Sections */}
        {SECTIONS.map(section => {
          const sectionLines = lines.map((l, gi) => ({ ...l, gi })).filter(l => (l.category_snapshot || "user_licenses") === section.key);
          const secProducts = products.filter(p => (p.category || "user_licenses") === section.key);
          const secMo = sectionLines.reduce((s, l) => s + lineMo(l), 0);
          const secYr = sectionLines.reduce((s, l) => s + lineYr(l), 0);
          const secOO = sectionLines.reduce((s, l) => s + lineOO(l), 0);
          return (
            <div key={section.key} className="border border-[#E5E5E5] overflow-hidden" data-testid={`section-${section.key}`}>
              <div className={`${section.color} px-5 py-3 flex items-center justify-between`}>
                <h2 className={`font-bold text-sm ${section.textCls} uppercase tracking-wider`}>{section.label} <span className="font-normal opacity-70 text-xs normal-case">{section.pricingLabel}</span></h2>
                <button type="button" onClick={() => addLine(section.key)} className="inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white font-bold uppercase tracking-wider" data-testid={`add-line-${section.key}`}><Plus size={13} weight="bold" /> Add</button>
              </div>
              <div className="bg-white p-4">
                {sectionLines.length === 0 ? (
                  <p className="text-xs text-[#A3A3A3] py-3 text-center">No items. Click "Add" above.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" style={{ minWidth: section.key === "services" ? 880 : 780 }}>
                      <thead><tr className="border-b border-[#E5E5E5]">
                        <th className="w-6"></th>
                        <th className="text-left pb-2 pr-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-32">Product</th>
                        <th className="text-left pb-2 pr-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-[240px]">Description</th>
                        <th className="text-left pb-2 pr-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-20">Type</th>
                        <th className="text-right pb-2 pr-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-[84px]">Qty</th>
                        <th className="text-right pb-2 pr-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-24">Price</th>
                        {section.key === "services" && (
                          <th className="text-right pb-2 pr-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-28" title="Optional ongoing monthly fee tied to this line. Not totalled at the section level — shown per-row only.">+ /Month</th>
                        )}
                        <th className="text-right pb-2 pr-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-20">Disc</th>
                        <th className="text-right pb-2 pr-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-24">/Month</th>
                        <th className="text-right pb-2 text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider w-24">/Year</th>
                        <th className="w-7"></th>
                      </tr></thead>
                      <tbody className="divide-y divide-[#F5F5F5]">
                        {sectionLines.map(({ gi, ...line }, posInSection) => {
                          const isOO = (line.pricing_type_snapshot || "annual") === "one_off";
                          const isFirst = posInSection === 0;
                          const isLast = posInSection === sectionLines.length - 1;
                          return (
                            <tr key={gi} data-testid={`line-item-${gi}`}>
                              <td className="py-2 pr-1 align-middle">
                                <div className="flex flex-col items-center gap-0.5">
                                  <button
                                    type="button"
                                    onClick={() => moveLine(gi, -1)}
                                    disabled={isFirst}
                                    className="text-[#A3A3A3] hover:text-[#0055FF] disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                                    title="Move up"
                                    data-testid={`move-up-${gi}`}
                                  ><CaretUp size={11} weight="bold" /></button>
                                  <button
                                    type="button"
                                    onClick={() => moveLine(gi, 1)}
                                    disabled={isLast}
                                    className="text-[#A3A3A3] hover:text-[#0055FF] disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                                    title="Move down"
                                    data-testid={`move-down-${gi}`}
                                  ><CaretDown size={11} weight="bold" /></button>
                                </div>
                              </td>
                              <td className="py-2 pr-2"><select value={line.product_id} onChange={e => onProductSelect(gi, e.target.value, section.key)} className="w-full border border-[#E5E5E5] px-2 py-1.5 text-xs focus:outline-none focus:border-[#0055FF]"><option value="">Custom...</option>{secProducts.map(p => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}</select></td>
                              <td className="py-2 pr-2"><input value={line.description_snapshot} onChange={e => setLine(gi, "description_snapshot", e.target.value)} required className="w-full border border-[#E5E5E5] px-2 py-1.5 text-xs focus:outline-none focus:border-[#0055FF]" /></td>
                              <td className="py-2 pr-2"><select value={line.pricing_type_snapshot || "annual"} onChange={e => setLine(gi, "pricing_type_snapshot", e.target.value)} className="w-full border border-[#E5E5E5] px-2 py-1.5 text-xs focus:outline-none focus:border-[#0055FF]"><option value="monthly">/mo</option><option value="annual">/yr</option><option value="one_off">1x</option></select></td>
                              {/* Signed values are intentional: a credit /
                                  rebate line is entered as a negative unit
                                  price (or a negative quantity). No `min`
                                  here — validity is enforced in submit()
                                  with an explanatory message rather than a
                                  silent browser block. */}
                              <td className="py-2 pr-2"><input type="number" step={0.5} value={line.quantity} onChange={e => setLine(gi, "quantity", e.target.value)} aria-label="Quantity (negative for a credit)" className="w-full border border-[#E5E5E5] px-2 py-1.5 text-xs text-right font-mono focus:outline-none focus:border-[#0055FF]" data-testid={`qty-input-${gi}`} /></td>
                              <td className="py-2 pr-2"><input type="number" step={0.01} value={line.unit_price} onChange={e => setLine(gi, "unit_price", e.target.value)} aria-label="Unit price (negative for a credit)" title="Enter a negative amount for a credit, rebate or discount line." className="w-full border border-[#E5E5E5] px-2 py-1.5 text-xs text-right font-mono focus:outline-none focus:border-[#0055FF]" data-testid={`unit-price-input-${gi}`} /></td>
                              {section.key === "services" && (
                                <td className="py-2 pr-2"><input type="number" min={0} step={0.01} value={line.monthly_addon_unit_price || 0} onChange={e => setLine(gi, "monthly_addon_unit_price", e.target.value)} placeholder="0.00" title="Optional ongoing monthly fee for this line. Leave 0 if not applicable." className="w-full border border-[#E5E5E5] px-2 py-1.5 text-xs text-right font-mono focus:outline-none focus:border-[#0055FF] placeholder:text-[#D4D4D4]" data-testid={`monthly-addon-input-${gi}`} /></td>
                              )}
                              <td className="py-2 pr-2"><input type="number" min={0} step={0.01} value={line.line_discount_amount} onChange={e => setLine(gi, "line_discount_amount", e.target.value)} className="w-full border border-[#E5E5E5] px-2 py-1.5 text-xs text-right font-mono focus:outline-none focus:border-[#0055FF]" /></td>
                              <td className="py-2 pr-2 text-right font-mono text-xs">{isOO ? <span className="text-[#E5E5E5]">-</span> : <span className="text-[#0A0A0A] font-semibold">{fmt(lineMo(line), form.currency_code)}</span>}</td>
                              <td className="py-2 text-right font-mono text-xs"><span className="text-[#0A0A0A] font-semibold">{isOO ? fmt(lineOO(line), form.currency_code) : fmt(lineYr(line), form.currency_code)}</span></td>
                              <td className="py-2"><button type="button" onClick={() => removeLine(gi)} className="text-[#A3A3A3] hover:text-red-500 p-0.5"><Trash size={13} /></button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {sectionLines.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#F5F5F5] flex flex-wrap items-center gap-x-5 gap-y-1 justify-end text-xs">
                    {/* != 0, not > 0 — a section that nets negative (a
                        credit section) must still show its subtotal. */}
                    {round2(secMo) !== 0 && <span className="text-[#A3A3A3]">Monthly: <span className="font-mono font-semibold text-[#525252]">{fmt(secMo, form.currency_code)}</span></span>}
                    {round2(secYr) !== 0 && <span className="text-[#A3A3A3]">Annual: <span className="font-mono font-semibold text-[#525252]">{fmt(secYr, form.currency_code)}</span></span>}
                    {round2(secOO) !== 0 && <span className="text-[#A3A3A3]">One-off: <span className="font-mono font-semibold text-[#525252]">{fmt(secOO, form.currency_code)}</span></span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Totals */}
        <div className="bg-white border border-[#E5E5E5] p-5">
          <div className="flex justify-end">
            <div className="w-80 space-y-1.5 text-sm">
              {round2(totalMo) !== 0 && <div className="flex justify-between text-[#525252]"><span>Monthly Recurring</span><span className="font-mono">{fmt(totalMo, form.currency_code)} <span className="text-[#A3A3A3] text-xs">/mo</span></span></div>}
              {round2(totalYr) !== 0 && <div className="flex justify-between text-[#525252]"><span>Annual Recurring</span><span className="font-mono">{fmt(totalYr, form.currency_code)} <span className="text-[#A3A3A3] text-xs">/yr</span></span></div>}
              {round2(totalOO) !== 0 && <div className="flex justify-between text-[#525252]"><span>One-off Total</span><span className="font-mono">{fmt(totalOO, form.currency_code)}</span></div>}
              {(round2(form.discount_amount) !== 0 || round2(form.tax_amount) !== 0) && (
                <div className="border-t border-[#F5F5F5] pt-1.5 space-y-1">
                  {round2(form.discount_amount) !== 0 && <div className="flex justify-between text-[#A3A3A3] text-xs"><span>Discount</span><span className="font-mono text-red-600">{fmt(-Number(form.discount_amount || 0), form.currency_code)}</span></div>}
                  {round2(form.tax_amount) !== 0 && <div className="flex justify-between text-[#A3A3A3] text-xs"><span>Tax</span><span className="font-mono">{fmt(form.tax_amount, form.currency_code)}</span></div>}
                </div>
              )}
              <div className="flex justify-between items-end pt-2 border-t-2 border-[#E5E5E5]">
                <div><div className="font-black text-[#0A0A0A] text-base">Annual Total</div><div className="text-[10px] text-[#A3A3A3]">Recurring/yr + one-off</div></div>
                <span className="font-mono font-black text-xl text-[#0055FF]">{fmt(annualTotal, form.currency_code)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white border border-[#E5E5E5] p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Internal Notes</label><textarea value={form.notes_internal} onChange={e => setF("notes_internal", e.target.value)} rows={3} className={inputCls} /></div>
            <div><label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">External Notes (client-facing)</label><textarea value={form.notes_external} onChange={e => setF("notes_external", e.target.value)} rows={3} className={inputCls} /></div>
          </div>
          <div>
            <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Standard Notes (T&Cs)</label>
            <textarea value={form.standard_notes} onChange={e => setF("standard_notes", e.target.value)} rows={6} className={inputCls + " font-mono text-xs"} data-testid="standard-notes-input" />
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>}
        <div className="flex gap-3">
          <button type="submit" disabled={saving || (validation && !validation.valid)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-quote-button"><FloppyDisk size={14} weight="bold" /> {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Proposal"}</button>
          <Link to="/quotes" className="px-5 py-2.5 text-sm font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
